import { mutate, useFileJet } from '@everlutionsk/filejet-react';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import { Avatar as MuiAvatar, SxProps, Theme, Typography, TypographyTypeMap } from '@mui/material';
import React, { ReactNode, useMemo } from 'react';

/**
 * Primary UI component for user interaction
 */

interface ImageData {
  readonly id: string;
}

const sizes = ['xxxs', 'xxs', 'xs', 's', 'm', 'l', 'xl', 'xxl'] as const;

type AvatarSizes = Record<
  (typeof sizes)[number],
  { textVariant: TypographyTypeMap['props']['variant']; styles: SxProps<Theme> }
>;

const sizesValues: AvatarSizes = {
  xxxs: {
    textVariant: 'subheadingXSmall',
    styles: { width: '24px', height: '24px', '& svg': { width: '12px', height: '12px' } }
  },
  xxs: {
    textVariant: 'subheadingXSmall',
    styles: { width: '26px', height: '26px', '& svg': { width: '13.714px', height: '13.714px' } }
  },
  xs: {
    textVariant: 'labelXSmall',
    styles: { width: '32px', height: '32px', '& svg': { width: '19px', height: '19px' } }
  },
  s: {
    textVariant: 'labelSmall',
    styles: { width: '40px', height: '40px', '& svg': { width: '24px', height: '24px' } }
  },
  m: {
    textVariant: 'labelMedium',
    styles: { width: '56px', height: '56px', '& svg': { width: '33.6px', height: '33.6px' } }
  },
  l: {
    textVariant: 'labelLarge',
    styles: { width: '64px', height: '64px', '& svg': { width: '40px', height: '40px' } }
  },
  xl: {
    textVariant: 'h3',
    styles: { width: '80px', height: '80px', '& svg': { width: '48px', height: '48px' } }
  },
  xxl: {
    textVariant: 'h2',
    styles: { width: '120px', height: '120px', '& svg': { width: '72px', height: '72px' } }
  }
};

const mutationValues = {
  xxxs: 'resize_34x34',
  xxs: 'resize_48x48',
  xs: 'resize_64x64',
  s: 'resize_80x80',
  m: 'resize_112x112',
  l: 'resize_128x128',
  xl: 'resize_160x160',
  xxl: 'resize_240x240'
};

interface CircularAvatarProps {
  /**
   * Size of the avatar
   */
  readonly size?: (typeof sizes)[number];
  /**
   * Src of the avatar
   * */
  readonly src?: string;
  /**
   * Initials of the avatar
   * @default ''
   * */
  readonly children?: ReactNode;
  /**
   * Custom styles
   * @default ''
   * */
  readonly sx?: SxProps<Theme>;
  /**
   * Alt of the avatar
   * */
  alt?: string;
}

export function CircularAvatar({ size = 'xxs', children, sx, ...props }: CircularAvatarProps) {
  const styles = { ...sizesValues[size].styles, ...sx };

  return (
    <MuiAvatar
      sx={{
        ...styles,
        '& svg': {
          display: 'block',
          transform: 'translateY(1px)',
          padding: '2px'
        }
      }}
      {...props}
    >
      {children && (
        <Typography sx={{ fontWeight: 900 }} variant={sizesValues[size].textVariant}>
          {children}
        </Typography>
      )}
      {!children && <PersonOutlinedIcon />}
    </MuiAvatar>
  );
}

export function FilejetCircularAvatar({
  image,
  ...props
}: CircularAvatarProps & { image?: ImageData | string | undefined }) {
  const filejet = useFileJet();
  const isRawSrc = typeof image === 'string';

  const mutation = [mutationValues[props.size ?? 'xxs']];

  const srcset = useMemo(() => {
    if (image == null) return;
    if (isRawSrc) return image;
    if (mutation == null) return filejet.url(mutate(image, 'auto'));

    return Object.entries(mutation)
      .map(
        ([key, mutation]) => `${filejet.url(mutate(image as ImageData, mutation, 'auto'))} ${key}`
      )
      .join(',');
  }, [image]);

  return <CircularAvatar {...props} src={srcset} />;
}
